/**
 * Штриховки стен по материалам — как на строительных чертежах.
 *
 * Лежат в разделе каталога «Стены», а не в виджете холста: одна и та же штриховка
 * нужна и на плане, и на развёртках — там ею закрашиваются стены в разрезе.
 *
 * Для каждого материала генерируется четыре паттерна: обычный, для несущей
 * стены (та же штриховка, но красная) и по одному «выделенному» варианту для
 * каждого из них. Шаг штриховки задан в экранных пикселях (через mmPerPx),
 * поэтому при зуме плотность остаётся читаемой и не превращается ни в сплошную
 * заливку, ни в редкие одиночные линии.
 */

import { BEARING_HATCH, CATALOG, type WallCatalogItem } from "@/shared/catalog";

interface Props {
	mmPerPx: number;
}

/** Идентификатор паттерна для материала — им заливается контур стены. */
export const wallPatternId = (material: string, active: boolean, bearing = false) =>
	`wall-${material}${bearing ? "--bearing" : ""}${active ? "--active" : ""}`;

function MaterialPattern({
	material,
	active,
	bearing,
	mmPerPx,
}: {
	material: WallCatalogItem;
	active: boolean;
	bearing: boolean;
	mmPerPx: number;
}) {
	const step = material.spacing * mmPerPx;
	const lineWidth = 0.8 * mmPerPx;
	// Выделение перекрывает роль: выделенная стена всегда синяя, чтобы её было видно
	const background = active ? "#e9f0fe" : bearing ? BEARING_HATCH.background : material.background;
	const stroke = active ? "#2f6feb" : bearing ? BEARING_HATCH.stroke : material.stroke;

	return (
		<pattern
			id={wallPatternId(material.material, active, bearing)}
			width={step}
			height={step}
			patternUnits="userSpaceOnUse"
			patternTransform={material.angle === null ? undefined : `rotate(${material.angle})`}
		>
			<rect width={step} height={step} fill={background} />
			{material.angle !== null && (
				<line x1={0} y1={0} x2={0} y2={step} stroke={stroke} strokeWidth={lineWidth} />
			)}
			{material.dots && (
				<circle cx={step / 2} cy={step / 2} r={lineWidth} fill={stroke} stroke="none" />
			)}
		</pattern>
	);
}

const VARIANTS: { active: boolean; bearing: boolean }[] = [
	{ active: false, bearing: false },
	{ active: false, bearing: true },
	{ active: true, bearing: false },
	{ active: true, bearing: true },
];

export const MaterialDefs = ({ mmPerPx }: Props) => {
	return (
		<defs>
			{VARIANTS.flatMap((variant) =>
				CATALOG.wall.items.map((material) => (
					<MaterialPattern
						key={wallPatternId(material.material, variant.active, variant.bearing)}
						material={material}
						active={variant.active}
						bearing={variant.bearing}
						mmPerPx={mmPerPx}
					/>
				)),
			)}
		</defs>
	);
};
